import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import DeleteConfirmation from "../../components/modal-popup/delete-popup";
import BazarProduct from "../../models/bazarProduct";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { Paginator, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";

const SubAdminPage: React.FC = () => {
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [subAdminData, setSubAdminData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [companyToDelete, setCompanyToDelete] = useState<any | null>(null)
    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("createddate");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [sortOrderapi, setSortOrderApi] = useState<string>("asc");
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const actionBodyTemplate = (rowData: BazarProduct) => {
        return (
            <div className="action-icons">
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} />
                {/* <Button label="Remove" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleDelete(rowData)} /> */}
            </div>
        );
    };

    async function getSubAdminData() {
        try {
            setDataLoad(true);

            // API endpoint with query params
            const params = new URLSearchParams({
                page: (currentPage + 1).toString(), // backend pages start from 1
                limit: itemsPerPage.toString(),
                search: name || "",
                sortField: sortField,
                sortOrder: sortOrderapi,
            });

            const response: any = await apiFunctions.get(`/subadmin/list?${params.toString()}`);

            if (response && response.data && response.data.success) {
                setSubAdminData(response.data.data);
                settotalRecords(response.data.totalRecords);

                const totalpage = Math.ceil(response.data.totalRecords / itemsPerPage);
                settotalPages(totalpage);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data?.message || "Failed to fetch subadmins",
                    life: 3000,
                });
            }
        } catch (error) {
            errorHandler(error as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const confirmDelete = async () => {
        if (companyToDelete) {
            try {
                await apiFunctions
                    .delete(`/user/delete/${companyToDelete._id}`)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        errorHandler(err);
                    })
                    .finally(() => {
                        setDeleteConfirmationVisible(false);
                        setCompanyToDelete(null);
                        setSortField("createddate");
                        setSortOrder(-1);
                        setCurrentPage(0);
                        getSubAdminData();
                    });
            } catch (error) {
                console.log(error);
            }
        }
    };

    const handleEdit = (rowData: any) => {
        navigate(`/admin/profiles/view/${rowData._id}`);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setCompanyToDelete(null);
    };

    const onSort = (e: any) => {
        const field = e.sortField;
        const order = e.sortOrder === 1 ? "asc" : "desc";

        setSortOrder(e.sortOrder);
        setSortField(field);
        setSortOrderApi(order);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
        } else {
            setName("");
            setLoad(false);
        }
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getSubAdminData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, name, sortField, sortOrder]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getSubAdminData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center table-header">
                {/* Title */}
                <div className="sm:col-span-3">
                    <h2 className="card-title m-0">Sub Admins</h2>
                </div>

                {/* Search */}
                <div className="sm:col-span-9 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <div className="search-box w-full sm:w-auto flex items-center gap-2">
                            <span className="whitespace-nowrap hidden sm:inline">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-64"
                                placeholder='Type here...'
                                onChange={getsearchdata}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",

        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="paginator-text">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },

        CurrentPageReport: () => {
            return <span className="entries-text ml-2">entries</span>;
        },
    };

    return (
        <>
            <div className="dashboard-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : subAdminData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <ShieldCheckIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No sub admins found</p>
                                </div>
                            ) : (
                                subAdminData.map((rowData: any, idx: number) => (
                                    <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name} {rowData.surname}</p>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div><span className="font-medium">District:</span> {rowData.district}</div>
                                            <div><span className="font-medium">Joined:</span> {formatDateTime(rowData.createddate)}</div>
                                            <div><span className="font-medium">Total Approvals:</span> {rowData.approvedCount}</div>
                                        </div>
                                        <div className="flex justify-end gap-2 mt-2">
                                            {actionBodyTemplate(rowData)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 260px)"
                        style={{ minHeight: "calc(100vh - 185px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _skeletonId: i }))
                                : subAdminData
                        }
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <ShieldCheckIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No sub admins found</p>
                            </div>
                        }
                    >
                        <Column field="name" header="Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.name} />
                        <Column field="surname" header="Last name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.surname} />
                        <Column field="district" header="District" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.district} />
                        <Column field="createddate" header="Joining Date" sortable
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : formatDateTime(rowData.createddate)
                            } />
                        <Column field="approvedCount" header="Total Approvals" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.approvedCount} />
                        <Column header="Action" className="action-column" frozen body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Confirm Delete"
                message="Are you sure you want to delete this Data?"
            />
        </>
    );
};

export default SubAdminPage;
